import { DateTime } from 'luxon';
import { type BulkIpDayUpdate, type ReadingPlanDay, ReadingPlanStatus } from '../dm/Plan.js';

/*
 * Pure functions for working with instantiated plans, shared by the
 * API (e.g. when subscribing) and the front end (which derives
 * everything from synced data, so it works offline).
 *
 * All dates are calendar dates (`yyyy-MM-dd`) with no time zone; a
 * day's reading belongs to a date, not to a moment in time. They're
 * parsed as UTC purely so that day arithmetic is never affected by
 * DST transitions.
 */

/**
 * Number of past, incomplete days after which a plan is shown as
 * "red" (more than this many) rather than "yellow" (more than 0)
 */
export const RED_STATUS_THRESHOLD = 10;

const fromIsoDate = (date: string): DateTime => DateTime.fromISO(date, { zone: 'utc' });

const isWeekend = (date: DateTime): boolean => date.weekday === 6 || date.weekday === 7;

/**
 * Returns `dayCount` consecutive dates, starting at `startDate`,
 * skipping Saturdays and Sundays if weekends aren't included. (If
 * `startDate` itself is on a weekend, the first date is the following
 * Monday.)
 */
export const generateSchedule = (startDate: string, dayCount: number, includeWeekends: boolean): string[] => {
  const dates: string[] = [];
  let current = fromIsoDate(startDate);

  for (let i = 0; i < dayCount; i++) {
    if (!includeWeekends) {
      while (isWeekend(current)) current = current.plus({ days: 1 });
    }
    dates.push(current.toISODate()!);
    current = current.plus({ days: 1 });
  }

  return dates;
};

/**
 * Minimal shape of an instantiated plan day needed by these helpers
 */
export interface IpDayLike {
  id: string;
  completed: boolean;
  scheduledDate: string;
}

export interface CatchUpScheduleParams {
  /**
   * All days for the instantiated plan, sorted by the plan days'
   * sequence numbers
   */
  days: IpDayLike[];

  /**
   * Date of the day being caught up from; if no day falls on that
   * date, the plan is caught up from its first day
   */
  fromDate: string;

  /**
   * The user's current local date; the from-day is moved here
   */
  today: string;

  /**
   * Whether days *before* the from-day should be marked as read
   */
  markPrevAsRead: boolean;

  includeWeekends: boolean;
}

/**
 * "Catches up" an instantiated plan: the from-day is moved to today,
 * subsequent days follow it, and earlier days are moved to the
 * (week)days immediately preceding it.
 *
 * Only *dates* are changed for the from-day and subsequent days;
 * whether they've been completed is left alone, so readings done
 * ahead of schedule are kept. Earlier days are additionally marked as
 * completed if `markPrevAsRead` is set.
 *
 * @returns Updates for only the days that actually changed, with only
 * the fields that changed
 */
export const catchUpSchedule = ({
  days,
  fromDate,
  today,
  markPrevAsRead,
  includeWeekends,
}: CatchUpScheduleParams): BulkIpDayUpdate[] => {
  if (days.length === 0) return [];

  let fromIndex = days.findIndex((d) => d.scheduledDate === fromDate);
  if (fromIndex === -1) fromIndex = 0;

  const newDates: string[] = new Array(days.length);
  const forwardDates = generateSchedule(today, days.length - fromIndex, includeWeekends);
  forwardDates.forEach((date, i) => (newDates[fromIndex + i] = date));

  let current = fromIsoDate(forwardDates[0]).minus({ days: 1 });
  for (let i = fromIndex - 1; i >= 0; i--) {
    if (!includeWeekends) {
      while (isWeekend(current)) current = current.minus({ days: 1 });
    }
    newDates[i] = current.toISODate()!;
    current = current.minus({ days: 1 });
  }

  const updates: BulkIpDayUpdate[] = [];
  days.forEach((day, i) => {
    const update: BulkIpDayUpdate = { id: day.id };
    if (newDates[i] !== day.scheduledDate) update.scheduledDate = newDates[i];
    if (markPrevAsRead && i < fromIndex && !day.completed) update.completed = true;

    if (update.scheduledDate !== undefined || update.completed !== undefined) updates.push(update);
  });

  return updates;
};

export interface IpStats {
  totalDays: number;
  completedDays: number;

  /**
   * Days scheduled before today that haven't been completed
   */
  incompletePastDays: number;

  /**
   * Fraction (0-1), rounded to 2 decimal places
   */
  percentComplete: number;

  status: ReadingPlanStatus;
}

/**
 * Progress for a single instantiated plan
 *
 * @param days All days for the instantiated plan
 * @param today The user's current local date
 */
export const getIpStats = (days: Pick<IpDayLike, 'completed' | 'scheduledDate'>[], today: string): IpStats => {
  const totalDays = days.length;
  const completedDays = days.filter((d) => d.completed).length;
  const incompletePastDays = days.filter((d) => !d.completed && d.scheduledDate < today).length;
  const percentComplete = totalDays === 0 ? 0 : Math.round(100 * (completedDays / totalDays)) / 100;

  const status: ReadingPlanStatus =
    incompletePastDays > RED_STATUS_THRESHOLD
      ? ReadingPlanStatus.red
      : incompletePastDays > 0
        ? ReadingPlanStatus.yellow
        : ReadingPlanStatus.normal;

  return { totalDays, completedDays, incompletePastDays, percentComplete, status };
};

/**
 * Minimal shapes of the synced data needed to build the readings for
 * a date
 */
export interface PlanLike {
  planInstanceId: string;
  name: string;
  days: { id: string; sequenceNum: number; osis?: string }[];
}
export interface IpLike {
  id: string;
  planInstanceId: string;
}
export interface IpDayWithPlanDay extends IpDayLike {
  instantiatedPlanId: string;
  planDayId: string;
}

/**
 * Builds the readings scheduled for a given date, across all of the
 * user's instantiated plans. Days with no passage are left out.
 *
 * @param date The date being viewed
 * @param today The user's current local date (for plan status)
 * @param plans Plans (with their days) for the instantiated plans
 * @param ips The user's (active) instantiated plans
 * @param days Days for those instantiated plans
 */
export const buildReadingPlanDaysForDate = (
  date: string,
  today: string,
  plans: PlanLike[],
  ips: IpLike[],
  days: IpDayWithPlanDay[]
): ReadingPlanDay[] => {
  const plansById = new Map(plans.map((p) => [p.planInstanceId, p]));
  const daysByIp = new Map<string, IpDayWithPlanDay[]>();
  for (const d of days) {
    const ipDays = daysByIp.get(d.instantiatedPlanId);
    if (ipDays) ipDays.push(d);
    else daysByIp.set(d.instantiatedPlanId, [d]);
  }

  const response: ReadingPlanDay[] = [];
  for (const ip of ips) {
    const plan = plansById.get(ip.planInstanceId);
    const ipDays = daysByIp.get(ip.id);
    if (!plan || !ipDays) continue;

    const planDaysById = new Map(plan.days.map((pd) => [pd.id, pd]));
    const stats = getIpStats(ipDays, today);

    for (const day of ipDays) {
      if (day.scheduledDate !== date) continue;

      const planDay = planDaysById.get(day.planDayId);
      if (!planDay?.osis) continue;

      response.push({
        ipId: ip.id,
        ipDayId: day.id,
        planName: plan.name,
        osisForDay: planDay.osis,
        dayCompleted: day.completed,
        date,
        readingPlanStatus: stats.status,
        sequenceNum: planDay.sequenceNum,
        totalDays: stats.totalDays,
        percentComplete: stats.percentComplete,
      });
    }
  }

  return response;
};

/**
 * First and last scheduled dates for a set of days (e.g. one
 * instantiated plan, or all of a user's), or `undefined` if there
 * are no days
 */
export const getIpDateRange = (days: Pick<IpDayLike, 'scheduledDate'>[]): { start: string; end: string } | undefined => {
  if (days.length === 0) return undefined;

  let start = days[0].scheduledDate;
  let end = days[0].scheduledDate;
  for (const d of days) {
    if (d.scheduledDate < start) start = d.scheduledDate;
    if (d.scheduledDate > end) end = d.scheduledDate;
  }

  return { start, end };
};

export type IpPhase = 'upcoming' | 'inProgress' | 'finished';

/**
 * Whether an instantiated plan hasn't started yet, is in progress, or
 * has finished (its last scheduled date has passed), as of `today`
 */
export const getIpPhase = (days: Pick<IpDayLike, 'scheduledDate'>[], today: string): IpPhase => {
  const range = getIpDateRange(days);
  if (!range) return 'finished';
  if (range.start > today) return 'upcoming';
  if (range.end < today) return 'finished';
  return 'inProgress';
};
